import { useEffect, useMemo, useRef, useState } from "react";
import { cardImage } from "@/lib/img";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import api from "@/lib/api";
import { useNavigate, Link } from "react-router-dom";
import { MapPin, ArrowRight, Plane, Sparkles, Star, Hotel, Ticket } from "lucide-react";
import { fold, citySlug } from "@/lib/travel";
import { cityImage } from "@/lib/cityImages";

export default function DestinationSearch({ cities, onDark = false, inline = false, idp = "hero" }) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const blurT = useRef(null);
  const items = useMemo(() => {
    const t = fold(q.trim());
    if (!t) return cities.slice(0, 6);
    const starts = [], has = [];
    for (const c of cities) {
      const n = fold(c.city), k = fold(c.country);
      if (n.startsWith(t)) starts.push(c);
      else if (n.replace(/[^a-z0-9]+/g, " ").includes(` ${t}`) || k.startsWith(t) || k.includes(` ${t}`)) has.push(c);
    }
    return [...starts, ...has].slice(0, 5);
  }, [cities, q]);
  const [dq, setDq] = useState("");
  useEffect(() => { const t = setTimeout(() => setDq(q.trim()), 160); return () => clearTimeout(t); }, [q]);
  const { data: extra } = useQuery({
    queryKey: ["travel-suggest", fold(dq)], enabled: dq.length >= 2, staleTime: 300_000, placeholderData: keepPreviousData,
    queryFn: () => api.get("/travel/suggest", { params: { q: dq } }).then((r) => r.data),
  });
  const hotels = q.trim().length >= 2 ? extra?.hotels || [] : [];
  const tours = q.trim().length >= 2 ? extra?.tours || [] : [];
  const flat = [...items.map((c) => ({ kind: "dest", c })), ...hotels.map((h) => ({ kind: "hotel", h })), ...tours.map((t) => ({ kind: "tour", t }))];
  const pick = (x) => {
    if (!x) return go(null);
    if (x.kind === "dest") return go(x.c);
    setOpen(false);
    window.open((x.h || x.t).url, "_blank", "noopener,noreferrer");
  };

  const go = (c) => { setOpen(false); navigate(`/travel/${c ? c.slug : citySlug(q.trim())}`); };
  const onKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setOpen(true); setHi((h) => Math.min(h + 1, flat.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
    else if (e.key === "Escape") setOpen(false);
  };
  const submit = (e) => {
    e.preventDefault();
    const x = flat[Math.min(hi, flat.length - 1)];
    if (x && (open || q.trim())) pick(x); else if (q.trim()) go(null);
  };

  return (
    <form onSubmit={submit} className={inline ? "w-full" : "mt-8 max-w-lg"} data-testid={`${idp}-travel-search`} role="search">
      <div className="flex flex-wrap items-center gap-2">
        {!inline && <span className="w-full sm:w-auto flex items-center gap-1.5 text-sm font-semibold text-white/90"><Plane className="w-4 h-4" /> Planning a trip?</span>}
        <div className="relative flex-1 min-w-[200px]">
          <MapPin className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
          <input value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); setHi(0); }} onKeyDown={onKey}
            onFocus={() => { clearTimeout(blurT.current); setOpen(true); }} onBlur={() => { blurT.current = setTimeout(() => setOpen(false), 150); }}
            placeholder={inline ? "Search a city, country, hotel or tour" : `Where to? ${cities.length || 330} destinations`} autoComplete="off" role="combobox" aria-expanded={open}
            aria-controls={`${idp}-dest-list`} data-testid={`${idp}-travel-input`}
            className={`w-full h-12 pl-10 pr-4 rounded-full bg-white text-ink-900 text-sm placeholder:text-neutral-400 focus:outline-none ${inline ? "border border-neutral-200 focus:border-brand shadow-sm" : "focus-visible:ring-2 focus-visible:ring-[#FFE14D]"}`} />
          {open && flat.length > 0 && (
            <ul id={`${idp}-dest-list`} role="listbox" data-testid={`${idp}-dest-suggestions`}
              className={`absolute z-30 left-0 ${inline ? "right-0" : "w-[calc(100vw-2rem)] max-w-[30rem]"} top-[calc(100%+6px)] bg-white rounded-2xl shadow-2xl shadow-black/20 border border-neutral-100 py-2 overflow-y-auto ${inline ? "max-h-[60vh]" : "max-h-[70vh]"}`}>
              {items.length > 0 && <Group label={q.trim() ? "Destinations" : "Popular destinations"} />}
              {items.map((c, i) => (
                <Row key={c.slug} i={i} hi={hi} setHi={setHi} onPick={() => pick(flat[i])} testid={`${idp}-dest-${c.slug}`}
                  img={c.image_url?.includes("unsplash") ? cityImage(c.slug, 120) : (c.thumb_url || c.image_url)} title={c.city} sub={c.country || "Destination"} />
              ))}
              {hotels.length > 0 && <Group label="Hotels" icon={Hotel} />}
              {hotels.map((h, j) => { const i = items.length + j; return (
                <Row key={h.id} i={i} hi={hi} setHi={setHi} onPick={() => pick(flat[i])} testid={`${idp}-hotel-${j}`} img={h.image_url} title={h.name} external
                  sub={<>{h.stars ? `${h.stars}★ · ` : ""}{h.city}{h.score ? <> · <Star className="inline w-3 h-3 -mt-0.5 fill-amber-400 text-amber-400" /> {h.score.toFixed(1)}</> : null}</>}
                  price={h.price && `from ${h.price}`} />); })}
              {tours.length > 0 && <Group label="Tours & activities" icon={Ticket} />}
              {tours.map((t, j) => { const i = items.length + hotels.length + j; return (
                <Row key={t.id} i={i} hi={hi} setHi={setHi} onPick={() => pick(flat[i])} testid={`${idp}-tour-${j}`} img={t.image_url} title={t.title} external
                  sub={<>{t.city}{t.rating ? <> · <Star className="inline w-3 h-3 -mt-0.5 fill-amber-400 text-amber-400" /> {t.rating}</> : null}</>} price={t.price && `from ${t.price}`} />); })}
            </ul>
          )}
        </div>
        {!inline && <button type="submit" data-testid="hero-travel-btn" className={`inline-flex items-center gap-2 h-12 px-6 rounded-full text-white font-semibold text-sm transition-colors ${onDark ? "bg-brand hover:bg-brand-hover" : "bg-ink-950 hover:bg-ink-800"}`}>Explore <ArrowRight className="w-4 h-4" /></button>}
      </div>
      {!inline && <Link to="/travel/quiz" data-testid="hero-quiz-link" className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-[#FFE14D] hover:underline">
        <Sparkles className="w-4 h-4" /> Not sure where to go? Take the 30-second quiz
      </Link>}
    </form>
  );
}

const Group = ({ label, icon: Icon }) => (
  <li className="px-4 pt-2 pb-1.5 text-[11px] font-bold uppercase tracking-wider text-neutral-400 flex items-center gap-1.5">{Icon && <Icon className="w-3.5 h-3.5" />}{label}</li>
);

function Row({ i, hi, setHi, onPick, testid, img, title, sub, price, external }) {
  return (
    <li role="option" aria-selected={i === hi}>
      <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={onPick} onMouseEnter={() => setHi(i)} data-testid={testid}
        data-aff-kind={external ? "travel" : undefined} data-aff-title={external ? title : undefined}
        className={`w-full flex items-center gap-3 px-3 py-2 text-left transition-colors ${i === hi ? "bg-brand/10 shadow-[inset_3px_0_0] shadow-brand" : ""}`}>
        {img ? <img src={cardImage(img, 160)} alt="" onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} className="w-10 h-10 rounded-lg object-cover bg-neutral-200 shrink-0" />
          : <span className="w-10 h-10 rounded-lg bg-neutral-200 shrink-0" />}
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold text-ink-950 truncate">{title}</span>
          <span className="block text-xs text-neutral-500 truncate">{sub}</span>
        </span>
        {price && <span className="shrink-0 text-xs font-bold text-ink-950 whitespace-nowrap">{price}</span>}
      </button>
    </li>
  );
}
